import { cva, type VariantProps } from "cva";
import { type ButtonHTMLAttributes, type ReactElement } from "react";

import { type IconProps } from "../Icon";
import styles from "./index.module.css";

export type IconColor = NonNullable<IconProps["color"]>;

export const variants = cva({
  base: styles.base,
  variants: {
    variant: {
      solid: styles.solid,
      filled: styles.filled,
      lighted: styles.lighted,
      subtle: styles.subtle,
    },
    color: {
      key: styles.key,
      weak: styles.weak,
    } as Record<IconColor | "weak" | "key", string>,
    shadow: {
      true: styles.shadow,
    },
    size: {
      s: styles.s,
    },
  },
  defaultVariants: {
    variant: "solid",
  },
});

type IconButtonBaseProps = VariantProps<typeof variants> & {
  children: ReactElement<Omit<IconProps, "color">>;
  number?: number;
  text?: string;
};

/**
 * IconButtonのprops型
 *
 * @remarks
 * colorプロパティの使用制限:
 * - variant="subtle" の場合: colorはIconColor型（primary, secondary, tertiary等）のみ使用
 * - variant="filled" または "lighted" の場合: colorは"weak"、"key"のみ使用
 * - variant="solid" の場合: colorは使用しない
 *
 * TypeScriptの型システムでvariantの値に基づいてcolorの型を動的に制限することは技術的に困難なため、
 * 型定義上は全てのcolor値が許容されていますが、上記のルールに従って使用してください。
 *
 * @example
 * ```tsx
 * <IconButton variant="subtle" color="primary">
 *   <AddIcon />
 * </IconButton>
 * ```
 */
export type IconButtonProps = IconButtonBaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "disabled"> & {
    disabled?: boolean;
  };

export type IconButtonLinkBaseProps = IconButtonBaseProps & {
  disabled?: boolean;
};
